/**
 * Integer-cent money parsing shared by the extraction pipeline and the
 * review-time recheck. All statement math runs on integer cents so the
 * one-cent reconciliation tolerance is exact; these two functions are the
 * only boundary between money strings and that arithmetic.
 */

// A comma directly before the final one or two digits is a decimal comma
// ("1.234,56", "1 234,56", "1234,56", "12,5"): US grouping always puts three
// digits after a comma, so no US amount ever ends this way. Stripping the
// comma instead reads "1.234,56" as 1.23456 and "12,5" as 125, errors of
// 1000x and 10x, so the shape is refused and the caller's unparseable-amount
// path handles it. A comma with three or more digits after it stays a
// thousands separator.
const decimalCommaPattern = /,\d{1,2}$/

/**
 * Parses a decimal money string into integer cents. Tolerates currency
 * symbols, US thousands separators, and accounting-style parentheses even
 * though the extraction prompt forbids them. Returns null for anything
 * non-numeric and for comma-decimal amounts, which this parser does not read.
 */
export const parseDecimalCents = (value: string | null): number | null => {
  if (value === null) return null

  let text = value.trim()
  if (text.length === 0) return null

  let negative = false
  const parenthesized = text.match(/^\((.+)\)$/)
  if (parenthesized) {
    negative = true
    text = parenthesized[1]!.trim()
  }

  text = text.replace(/[$€£\s]/g, "")

  if (decimalCommaPattern.test(text)) return null

  // Dot-grouped thousands ("1.234.567") survive this strip with two dots and
  // fail the numeric match below, so they need no separate check.
  text = text.replace(/,/g, "")

  if (text.startsWith("+")) text = text.slice(1)
  if (text.startsWith("-")) {
    negative = !negative ? true : negative
    text = text.slice(1)
  }

  const match = text.match(/^(\d+)(?:\.(\d+))?$/)
  if (!match) return null

  const whole = Number(match[1])
  const fractionDigits = match[2] ?? ""
  // Half-up on the third decimal, done on the digits themselves. Rounding the
  // float instead is wrong for a handful of values: Number("0.145") * 100 is
  // 14.499999999999998, which rounds to 14 where exact decimal arithmetic
  // gives 15 (0.285, 0.565 and 0.575 fail the same way). Extraction only ever
  // produces two decimals, but a reviewer can type a sub-cent amount, and a
  // one-cent error is exactly the size of the reconciliation tolerance.
  //
  // The remainder past two decimals is >= half a cent precisely when the
  // third digit is >= 5, whatever follows it, so one digit decides the carry.
  const centsFraction =
    fractionDigits.length === 0
      ? 0
      : Number(fractionDigits.slice(0, 2).padEnd(2, "0")) +
        (Number(fractionDigits[2] ?? "0") >= 5 ? 1 : 0)

  if (!Number.isSafeInteger(whole * 100)) return null

  const cents = whole * 100 + centsFraction
  // "(0.00)" and "-0.00" are zero, not negative zero: centsToDecimalString
  // writes both back as "0.00", so a -0 here would break the round trip under
  // strict equality.
  return negative && cents !== 0 ? -cents : cents
}

export const centsToDecimalString = (cents: number | null): string | null => {
  if (cents === null) return null
  const sign = cents < 0 ? "-" : ""
  const absolute = Math.abs(cents)
  const whole = Math.floor(absolute / 100)
  const fraction = String(absolute % 100).padStart(2, "0")
  return `${sign}${whole}.${fraction}`
}
